import type { QueryClient } from '@tanstack/react-query';
import { calculateCustomBetFilter } from '@/api/custom-bets';
import { eventBetBuilderQuery, type EventDetailsDto } from '@/api/queries';
import { queryKeys } from '@/api/query-keys';
import {
  buildCalculateRequestSelections,
  findCustomItemIndex,
} from '@/domain/custom-bets';
import type { BetslipTicket } from '@/domain/betslip';
import {
  betslipApplyCustomOdd,
  betslipSetPossibleWinPending,
  betslipUndoLastCustomSelection,
  getBetslipTicket,
} from './betslip-store';
import {
  clearCustomBetAvailableSelections,
  setCustomBetAvailableSelections,
} from './custom-bet-availability-store';

/** Matches `eventBetBuilderQuery` staleTime — refetch when cache is older. */
const BET_BUILDER_STALE_MS = 15_000;

function customPackageEventIds(ticket: BetslipTicket): string[] {
  const ids: string[] = [];
  for (const item of ticket.Items) {
    if (item.Bets.some((bet) => bet.Custom === true)) {
      ids.push(String(item.Event.EventId));
    }
  }
  return ids;
}

function shouldRefetchBetBuilder(queryClient: QueryClient, eventId: string): boolean {
  const state = queryClient.getQueryState<EventDetailsDto>(queryKeys.sports.betBuilder(eventId));
  if (!state?.dataUpdatedAt) return true;
  if (Date.now() - state.dataUpdatedAt > BET_BUILDER_STALE_MS) return true;

  const expireRaw = state.data?.ExpireOdds ?? state.data?.LastEdit;
  if (typeof expireRaw === 'string' && expireRaw.trim()) {
    const expireMs = Date.parse(expireRaw);
    if (Number.isFinite(expireMs) && Date.now() > expireMs) return true;
  }
  return false;
}

async function repriceCustomPackage(eventId: string): Promise<void> {
  const ticket = getBetslipTicket();
  const itemIndex = findCustomItemIndex(ticket, eventId);
  if (itemIndex < 0) {
    clearCustomBetAvailableSelections();
    return;
  }

  const bet = ticket.Items[itemIndex]!.Bets.find((entry) => entry.Custom === true);
  const selections = bet?.Selections ?? [];
  if (selections.length === 0) {
    betslipApplyCustomOdd(eventId, null);
    betslipSetPossibleWinPending(false);
    clearCustomBetAvailableSelections();
    return;
  }

  betslipSetPossibleWinPending(true);
  try {
    const response = await calculateCustomBetFilter({
      Selections: buildCalculateRequestSelections(selections),
    });
    if (response.Odds === 0) {
      betslipUndoLastCustomSelection(eventId);
      clearCustomBetAvailableSelections();
      return;
    }
    setCustomBetAvailableSelections(response.AvailableSelections ?? null);
    if (selections.length < 2) {
      betslipApplyCustomOdd(eventId, null);
      betslipSetPossibleWinPending(true);
      return;
    }
    betslipApplyCustomOdd(eventId, response.Odds);
  } catch {
    betslipSetPossibleWinPending(false);
  }
}

/**
 * Before place: refresh stale Bet Builder markets and reprice Custom packages
 * via CBAPI (handover §6.7 / §9 — do not rely on multiplied singles).
 */
export async function refreshCustomPackagesBeforePlace(
  queryClient: QueryClient,
  ticket: BetslipTicket,
): Promise<void> {
  const eventIds = customPackageEventIds(ticket);
  if (eventIds.length === 0) return;

  await Promise.all(
    eventIds.map(async (eventId) => {
      if (!shouldRefetchBetBuilder(queryClient, eventId)) return;
      await queryClient.fetchQuery({
        ...eventBetBuilderQuery(eventId),
        staleTime: 0,
      });
    }),
  );

  for (const eventId of eventIds) {
    await repriceCustomPackage(eventId);
  }
}
